import { Injectable } from "@angular/core";
import * as m from "moment-timezone";
import { Observable, Subject, of, defer } from "rxjs";
import { tap, switchMap, filter, take } from "rxjs/operators";
import { UserService } from "src/app/authentication/user.service";
import {
  FormUserCompletionResponse,
  PhodaFormsService,
  SettingsService,
  UserSettingDto,
  UserSettingsSaveDbRequest,
  ResponseStatus,
  ResponseTransportOfUserSettingDto,
} from "../../../swagger/api.client.generated";
import { UserSettingType } from "./user-setting-type.enum";
import { SnackbarService } from "@app/common/snackbar-custom/snackbar.service";
import {
  ResponseTransportOfUserSettingItem,
  UserSettingItem,
} from "../score/score.model";
import { WebPushSubscriptionService } from "./webpush-subscription.service";
import { NativePushNotificationService } from "../../services/native-push-notification.service";
@Injectable({
  providedIn: "root",
})
export class SettingsLocalService {
  userSettings: UserSettingDto = new UserSettingDto();
  private filterChangedSource = new Subject();
  private isLoaded: boolean = false;
  onSourceChanged = this.filterChangedSource.asObservable();
  formUserCompletion: FormUserCompletionResponse =
    new FormUserCompletionResponse();

  constructor(
    private settingService: SettingsService,
    private phodaFormsService: PhodaFormsService,
    private userService: UserService,
    private snackbarService: SnackbarService,
    private webPushSubscriptionService: WebPushSubscriptionService,
    private nativePushNotificationService: NativePushNotificationService
  ) {}

  get ServiceLoaded(): boolean {
    return this.isLoaded;
  }

  getSettings(): UserSettingDto {
    return this.userSettings;
  }

  /**
   * Get settings as an observable that waits for settings to be loaded if not already loaded.
   * Returns settings immediately if already loaded, otherwise waits for them to load.
   * If settings haven't been initialized yet, triggers initialization.
   * @returns Observable that emits UserSettingDto when settings are available
   */
  getSettings$(): Observable<UserSettingDto> {
    return defer(() => {
      // If settings are already loaded, return them immediately
      if (this.isLoaded) {
        return of(this.userSettings);
      }

      // If not loaded, check if we need to trigger initialization
      // (in case init() hasn't been called yet)
      if (!this.isLoaded) {
        // Trigger settings load if not already in progress
        // We check if a load is in progress by seeing if onSourceChanged has subscribers
        // If not, we trigger the load
        this.getSettingsFromDb();
      }

      // Wait for settings to load via onSourceChanged
      // This will emit when getSettingsFromDb completes
      return this.onSourceChanged.pipe(
        filter(() => this.isLoaded), // Only proceed when settings are actually loaded
        take(1), // Take only the first emission
        switchMap(() => of(this.userSettings)) // Return the loaded settings
      );
    });
  }
  getSoundsOn(): boolean {
    return this.userSettings.audioIsOn;
  }
  getScoreNotificationIsOn(): boolean {
    return this.userSettings.isMarginOfSafetyAlertOn;
  }

  getBsbNotificationIsOn(): boolean {
    return this.userSettings.isBrainScienceBiteAlertOn;
  }

  saveSettings(settings: UserSettingsSaveDbRequest, message?: string) {
    // Return the observable so the caller can handle subscription management
    return this.settingService.saveSettings(settings).pipe(
      tap((res) => {
        this.refreshSettingsFromDb();
        const snackbarMessage =
          message === undefined ? res.responseMessage : message;
        this.snackbarService.openDesignSnackbar("Changes saved", snackbarMessage, 3000);
      })
    );
  }

  /**
   * Save settings without automatically refreshing from database
   * This is useful when we need to manage subscriptions first before refreshing
   */
  saveSettingsWithoutRefresh(settings: UserSettingsSaveDbRequest, message?: string) {
    // Return the observable without automatic refresh
    return this.settingService.saveSettings(settings).pipe(
      tap((res) => {
        const snackbarMessage =
          message === undefined ? res.responseMessage : message;
        this.snackbarService.openDesignSnackbar("Changes saved", snackbarMessage, 3000);
      })
    );
  }

  /**
   * Get a specific user setting value by name from cached settings
   * @param name The name of the setting to retrieve (e.g., "Plan.ZoomPercentage", "TimeZone")
   * @returns Observable with the setting value wrapped in ResponseTransport
   */
  getUserSetting(name: string): Observable<ResponseTransportOfUserSettingItem> {
    return new Observable((observer) => {
      // If settings aren't loaded yet, load them first
      if (!this.isLoaded) {
        this.settingService.get().subscribe((settings) => {
          this.userSettings = settings;
          this.isLoaded = true;
          observer.next(this.getSettingByName(name));
          observer.complete();
        });
      } else {
        observer.next(this.getSettingByName(name));
        observer.complete();
      }
    });
  }

  /**
   * Helper method to map setting name to value from cached userSettings
   */
  private getSettingByName(name: string): ResponseTransportOfUserSettingItem {
    const result = new UserSettingItem({
      name: name,
      userGuid: this.userSettings.userGuid,
      value: null,
      type: UserSettingType.Boolean,
    });

    // Map from UserSettingDto properties to setting names
    switch (name) {
      case "Plan.ZoomPercentage":
        // This would come from a future implementation or localStorage for now
        result.value = localStorage.getItem(name) || "100";
        result.type = UserSettingType.Number;
        break;
      case "TimeZone":
        result.value = this.userSettings.timezone;
        result.type = UserSettingType.Time;
        break;
      case "ScoreAlert.IsEnabled":
        result.value = this.userSettings.isMarginOfSafetyAlertOn?.toString();
        result.type = UserSettingType.Boolean;
        break;
      case "LoadPreviousHalos":
        result.value = this.userSettings.loadPreviousHalos?.toString();
        result.type = UserSettingType.Boolean;
        break;
      case "LoadPreviousZombies":
        result.value = this.userSettings.loadPreviousZombies?.toString();
        result.type = UserSettingType.Boolean;
        break;
      default:
        result.value = null;
    }

    const response = new ResponseTransportOfUserSettingItem({
      status: ResponseStatus.Success,
      result: result,
    });

    return response;
  }

  /**
   * Add or update a user setting and refresh from DB
   * @param name Setting name (e.g., "Plan.ZoomPercentage")
   * @param value Setting value
   * @param type Setting type (Boolean, Time, Number)
   * @returns Observable with the update response
   */
  addOrUpdateUserSetting(
    name: string,
    value: string,
    type: UserSettingType,
    updateLocalStorage: boolean = true
  ): Observable<ResponseTransportOfUserSettingDto> {
    if (updateLocalStorage) {
      localStorage.setItem(name, value);
    }

    return this.settingService.addOrUpdateUserSetting(name, value, type.toString()).pipe(
      tap((response: ResponseTransportOfUserSettingDto) => {
        // Refresh all settings from DB after update
        this.refreshSettingsFromDb();
      })
    );
  }

  /**
   * Refresh settings from database after any update
   * This ensures the local cache is always in sync with the DB
   */
  refreshSettingsFromDb() {
    this.getSettingsFromDb();
  }

  init() {
    this.getSettingsFromDb();
    this.getUserFormCompletionFromDb();
  }

  refreshData() {
    this.getLatestDataFromDb();
  }

  private getSettingsFromDb() {
    this.settingService.get().subscribe((settings) => {
      if (settings == null) {
        // The generated client's return type lies here - a 204 response resolves to a null
        // body. The backend now self-heals a missing UserSettings row, but keep this guard so
        // a null response degrades gracefully (keeps the previous/default UserSettingDto)
        // instead of crashing every consumer of getSettings().
        console.warn("[Settings] GET returned null - keeping previous settings");
        this.isLoaded = true;
        this.filterChangedSource.next();
        return;
      }
      this.userSettings = settings;
      this.filterChangedSource.next();
      this.isLoaded = true;
      // FLIP-464: a user with no saved timezone gets the browser/device one saved on first login.
      // Empty and whitespace count as "none" too - a blank string would otherwise skip this and
      // leave reminders evaluated against the server fallback zone.
      if (!this.userSettings.timezone || !this.userSettings.timezone.trim()) {
        this.userSettings.timezone = m.tz.guess();
        this.settingService
          .saveUserTimezone(this.userSettings.timezone)
          .subscribe();
      }

      // Initialize WebPush subscription based on device-specific subscription status
      // IMPORTANT: Only use device-specific status, NOT global preferences
      // Global preferences (isBrainScienceBitePushOn) are shared across all devices
      // Device-specific status tells us if THIS device is actually subscribed
      if (this.webPushSubscriptionService.isSupported()) {
        // Use device-specific subscription status from backend
        // For web browsers: check WebPush subscription
        // For mobile apps: WebPush is not used (they use native push), so skip initialization
        const isRunningInMobileApp = this.nativePushNotificationService.isRunningInMobileApp();
        
        if (!isRunningInMobileApp) {
          // Only initialize WebPush for web browsers, not mobile apps
          const isDeviceSubscribedFromBackend = 
            this.userSettings.isDeviceSubscribedForWebPush ?? null;
          
          // Pass null for preferences - we only care about device subscription status
          // If device is not subscribed in backend, do NOT subscribe automatically
          this.webPushSubscriptionService.initializeFromSettings(
            null, // Don't use global preferences during initialization
            null, // Don't use global preferences during initialization
            isDeviceSubscribedFromBackend
          );
        }
      }

      // Send subscription status to native app after settings are loaded
      // This syncs the native app with the current web app subscription state
      console.log(
        "[Settings] Settings loaded, notifying native app"
      );
      this.nativePushNotificationService.sendSubscriptionStatusToNative();
    });
  }

  private getUserFormCompletionFromDb() {
    this.phodaFormsService
      .getFormUserCompletion(this.userService.getUser().userGuid)
      .subscribe((data) => {
        this.userService.formUserCompletion = data;
        this.formUserCompletion = data;
      });
  }

  private getLatestDataFromDb() {
    this.settingService.get().subscribe((settings) => {
      if (settings != null) {
        this.userSettings = settings;
      }
    });
  }

}
